/**
 * Who each pillar is, as a screen shows it — slice 65.
 *
 * Slice 65 split the old Reach into **Reach** (paid ads on the workspace's own
 * Meta ad account) and **Connect** (email and WhatsApp to the people the
 * workspace holds), beside **Sense**. From then on a pillar is named in at
 * least four places — the sidebar's group headings, the Overview's pillar
 * strip, the eyebrow on each pillar's pages, and the badge on a Sense finding
 * saying which pillar its action hands to — and each place needs the same
 * three or four things: the name, the glyph, one line on what it is for, and
 * where it starts. This module is the only place those are written.
 *
 * The rejected alternative was to let each screen carry its own copy, which is
 * how it started: `AppLayout.tsx` had a `PILLAR_LABELS` map and the Overview's
 * `PillarSection` had its own words. Two copies of a pillar's name drift the
 * first time somebody renames one, and a sidebar that says "Connect" over an
 * Overview card that says something else reads as two different things.
 *
 * What is **not** here, on purpose:
 *
 *   - **Whether a pillar exists, or is on this plan.** That is `@wend/shared`'s
 *     `PILLAR_IS_BUILT` and `overviewPillar`, one verdict the sidebar and the
 *     Overview both read. This file says what a pillar is called, never whether
 *     it may be shown.
 *   - **A pillar that is not built.** Until slices 68–70 that was Flow, and
 *     this file deliberately gave it no glyph or purpose line — a roadmap drawn
 *     as an interface (slice 26). Flow shipped (engine, builder, and the Sense
 *     and Reach hand-offs), so it has an identity below like the others;
 *     {@link PILLAR_IDENTITY} is still keyed by {@link BuiltPillar}, so the
 *     next unbuilt pillar, if there is one, cannot be looked up either.
 *   - **The section list.** Which screens sit under a pillar is
 *     `PILLAR_SECTIONS` in `@wend/shared`; the sidebar's per-screen labels and
 *     paths are `NAV_DESTINATIONS` in `AppLayout.tsx`. `landing` here is the one
 *     place a pillar *starts*, which is what a card or a strip links to.
 *
 * Icons are elements, not components, as `NAV_DESTINATIONS`' are: every place
 * that draws one sizes it from the container with `[&_svg]` selectors, which is
 * how this design system has sized glyphs since slice 0.
 */
import { PILLAR_IS_BUILT, PILLAR_ORDER, type Feature, type PillarState } from '@wend/shared';
import { Library, Megaphone, MessagesSquare, Radar, Workflow } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The pillars that exist as software.
 *
 * Spelled as a type because a type cannot be read off `PILLAR_IS_BUILT`, which
 * is a runtime map. Until slice 70 it was `Exclude<Feature, 'flow'>`; Flow
 * shipped, `PILLAR_IS_BUILT.flow` flipped, and this line changed with it — and
 * so did {@link PILLAR_IDENTITY}, which stopped compiling until Flow had a
 * purpose line and a landing, exactly as this comment said it would.
 */
export type BuiltPillar = Feature;

export interface PillarIdentity {
  /** The name, as a heading, an eyebrow or a badge prints it. */
  readonly label: string;
  /** The pillar's glyph, as an element; decorative wherever it is drawn. */
  readonly icon: ReactNode;
  /** One line, from the reader's side: what this pillar does for them. */
  readonly purpose: string;
  /** Where the pillar starts — what a card or a strip links to. */
  readonly landing: string;
}

/**
 * Each built pillar, in the words every screen uses.
 *
 * "Meta ad account" in Reach's line, never the bare "Meta account": the
 * WhatsApp side is also "your own Meta account" (the number's), and a reader
 * told both would assume one account covers both and meet a second at
 * onboarding. It is the workspace's own ad account — Wend drafts and controls
 * ads there and never pays for them. Connect's "the people
 * you hold" is the owned-channel claim: these are contacts the workspace
 * uploaded, messaged from its own SES and WhatsApp accounts. Sense's line is
 * the Today queue's promise, stated as a question and its answer.
 */
export const PILLAR_IDENTITY: Readonly<Record<BuiltPillar, PillarIdentity>> = {
  reach: {
    label: 'Reach',
    icon: <Megaphone />,
    purpose: 'Paid ads on your own Meta ad account.',
    landing: '/reach/ads',
  },
  connect: {
    label: 'Connect',
    icon: <MessagesSquare />,
    purpose: 'Email and WhatsApp to the people you hold.',
    landing: '/campaigns',
  },
  sense: {
    label: 'Sense',
    icon: <Radar />,
    purpose: 'What changed, and what to do about it.',
    landing: '/sense/today',
  },
  // Slice 70. "Journeys" is the admin console's word for what is being granted;
  // the line says what a journey is made of, and that it runs on its own.
  flow: {
    label: 'Flow',
    icon: <Workflow />,
    purpose: 'Journeys that run themselves: triggers, waits, branches.',
    landing: '/flows',
  },
};

/**
 * Every pillar's bare name.
 *
 * For the `Record<Feature, …>` places that must answer for every member of the
 * enum — a sidebar group built from `navigationTree`, a finding's target pillar
 * typed as `Feature`. Read from {@link PILLAR_IDENTITY} so there is one
 * spelling. (Until slice 70 Flow's name was written here alone, because Flow
 * had no identity; superseded.)
 */
export const PILLAR_LABELS: Readonly<Record<Feature, string>> = {
  reach: PILLAR_IDENTITY.reach.label,
  connect: PILLAR_IDENTITY.connect.label,
  sense: PILLAR_IDENTITY.sense.label,
  flow: PILLAR_IDENTITY.flow.label,
};

/**
 * True when a pillar is built **and** has an identity here.
 *
 * Both halves on purpose. `PILLAR_IS_BUILT` alone would let a pillar flipped to
 * built in `@wend/shared`, before this file was edited, through to a lookup
 * that returns `undefined`; the `in` check alone would ignore the shared
 * verdict. Failing either one keeps the pillar off the screen, which is the
 * direction slice 26 chose for every disagreement of this kind.
 */
export function isBuiltPillar(feature: Feature): feature is BuiltPillar {
  return PILLAR_IS_BUILT[feature] && feature in PILLAR_IDENTITY;
}

/**
 * Whether a pillar is drawn at all, as a sidebar group or on the Overview —
 * slice 65's rule, and the one place it is written.
 *
 * Built first (slice 26: an unbuilt pillar is never drawn — Flow was, until
 * slice 70), and then either **Connect**,
 * always, or a pillar that is **on this plan**. Connect is the exception
 * because the Overview's dashboard is its content: a workspace without it
 * needs one line saying why its first screen is empty. An unentitled Reach or
 * Sense explains nothing on this workspace's screens, so it is absent rather
 * than a heading with "Not on this plan" under it, which reads as an upsell
 * (slice 59's rule for Sense, and Reach's since the split).
 *
 * The sidebar (`AppLayout.tsx`) and the Overview's strip (`PillarStrip.tsx`)
 * both ask this, so they cannot disagree about which pillars a workspace is
 * shown. They differ only in what they draw for the one pillar this lets
 * through unentitled — the sidebar a "Not on this plan" group, the Overview
 * its `PillarSection` — and the strip is the narrower of the two by design
 * (see its header).
 */
export function pillarIsShown(feature: Feature, state: PillarState): feature is BuiltPillar {
  if (!isBuiltPillar(feature) || state === 'not_built') return false;
  return feature === 'connect' || state === 'available';
}

/** The built pillars in display order — Reach, Connect, Sense, Flow (Flow since slice 70). */
export const BUILT_PILLARS: readonly BuiltPillar[] = PILLAR_ORDER.filter(isBuiltPillar);

/**
 * The shared layers' group — Audiences, Segments, Templates — as the sidebar
 * heads it. Not a pillar (see `NAV_SHARED_SECTIONS` in `@wend/shared`), so it
 * has no purpose line or landing; it is here so its glyph sits beside the
 * pillars' and is chosen once. Workspace has no glyph: it is the machinery,
 * and a heading without one reads as quieter, which is right.
 */
export const SHARED_GROUP: Readonly<{ label: string; icon: ReactNode }> = {
  label: 'Audiences & templates',
  icon: <Library />,
};
